import { motion } from 'framer-motion';
import { profile } from '../../data/profile';
import { ui } from '../../data/ui';
import { Button } from '../ui/Button';
import './Hero.css';

const socialIcons: Record<string, string> = {
  linkedin: 'in',
  github: 'GH',
  email: '@',
  twitter: 'X',
  other: '↗',
};

export function Hero() {
  const isPhotoPlaceholder = !profile.photo || profile.photo.includes('[');

  return (
    <section className="hero">
      <div className="bg-glow hero-glow-1" />
      <div className="bg-glow hero-glow-2" />
      <div className="container hero-inner">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.span
            className="hero-greeting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            {profile.hero.greeting}
          </motion.span>

          <h1 className="hero-heading">
            <span className="hero-heading-line">{profile.hero.heading}</span>
          </h1>

          <div className="hero-meta">
            <span className="hero-meta-item hero-meta-highlight">
              {profile.yearsOfExperience}+ {ui.hero.yearsExperience}
            </span>
            <span className="hero-meta-divider">·</span>
            <span className="hero-meta-item">
              {ui.hero.since} {profile.careerStartYear}
            </span>
            <span className="hero-meta-divider">·</span>
            <span className="hero-meta-item">{profile.location}</span>
          </div>

          <p className="hero-role">{profile.title}</p>
          <p className="hero-subtitle">{profile.hero.subtitle}</p>

          <div className="hero-intro">
            {profile.hero.introduction.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="hero-actions">
            <Button to="/projects" variant="primary">{ui.hero.viewWork}</Button>
            <Button to="/contact" variant="secondary">{ui.hero.contactMe}</Button>
            <Button to="/resume" variant="secondary">{ui.hero.viewResume}</Button>
          </div>

          <div className="hero-social">
            {profile.socialLinks.map((link) => {
              const isPlaceholder = link.url.startsWith('[');
              const content = (
                <>
                  <span className="social-icon">{socialIcons[link.icon]}</span>
                  <span className="social-name">{link.name}</span>
                </>
              );
              if (isPlaceholder) {
                return (
                  <span key={link.name} className="hero-social-link hero-social-placeholder">
                    {content}
                  </span>
                );
              }
              return (
                <a
                  key={link.name}
                  href={link.url}
                  className="hero-social-link"
                  target={link.url.startsWith('http') ? '_blank' : undefined}
                  rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={link.name}
                >
                  {content}
                </a>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <div className="hero-profile-card glass-card">
            <div className="hero-photo-wrap">
              {!isPhotoPlaceholder ? (
                <img
                  src={profile.photo}
                  alt={profile.name}
                  className="hero-photo"
                  loading="eager"
                />
              ) : (
                <div className="placeholder-image hero-photo-placeholder">
                  {ui.hero.addPhoto}
                </div>
              )}
              <div className="hero-photo-ring" />
            </div>
            <div className="hero-profile-info">
              <h2 className="hero-profile-name">{profile.name}</h2>
              <p className="hero-profile-title">{profile.title}</p>
              <div className="hero-profile-stats">
                <div className="hero-stat">
                  <span className="hero-stat-value">{profile.yearsOfExperience}+</span>
                  <span className="hero-stat-label">{ui.hero.yearsExperience}</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-value">{profile.careerStartYear}</span>
                  <span className="hero-stat-label">{ui.hero.careerStart}</span>
                </div>
              </div>
              <p className="hero-profile-quote">{profile.tagline}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
